import type { PrimitiveValueOrArray } from '@/data-viz/data-source.interface';
import type { UniverseId } from '@/domain/universes/universe.interface';
import type { AlertScenarioId, AlertStatPeriodCode } from '@/features/alerts/alerts.interface';
import type { BaseloadKPI } from '@/features/baseload-analysis/baseload-analysis.interface';
import type { ExportId } from '@/features/exports/exports.interface';
import type { ReportPlan } from '@/features/reports/reports.interface';
import type { SenAnalysisModelId } from '@/features/sen-analysis/analysis-models/sen-analysis-model.interface';
import type { BusinessIntelligenceFilter } from '@/interfaces/BusinessIntelligence.interface';
import type { ClassificationNodeId } from '@/interfaces/Classification.interface';
import type { CompletenessResourceType } from '@/interfaces/Completeness.interface';
import type { UnzonedDateTime } from '@/interfaces/Date.interface';
import type { CompletenessTimeStep } from '@/interfaces/DeliveryPointIndicator.interface';
import type { LegacyDashboardRight } from '@/providers/rights.constants';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import type { NavigationExtras } from '@angular/router';
import type { Observable } from 'rxjs';
import type { BuildingId } from '../../interfaces/Building.interface';
import type { BusinessIndicatorId } from '../../interfaces/BusinessIndicator.interface';
import type { DeliveryPointId } from '../../interfaces/DeliveryPoint.interface';
import type { ImportResourceType } from '../../interfaces/ImportHistory.interface';
import type { MeterId } from '../../interfaces/Meter.interface';
import type { CoreSystemWithSensors, SystemId } from '../../interfaces/System.interface';
import type { DeliveryPointMetric, SensorTimeseriesId, TimeseriesIndicatorCode } from '../../interfaces/Timeseries.interface';
/**
 * @deprecated
 */
export const Page = {
    LOGIN: 'LOGIN',
    ACCOUNT_CREATION: 'ACCOUNT_CREATION',
    DASHBOARDS: 'DASHBOARDS',
    BUILDING_CREATION: 'BUILDING_CREATION',
    NO_BUILDING_ACCESS_ERROR: 'NO_BUILDING_ACCESS_ERROR',
    THRESHOLD_SCENARIO_LANDING: 'THRESHOLD_SCENARIO_LANDING',
    THRESHOLD_SCENARIO_EDITION: 'THRESHOLD_SCENARIO_EDITION',
    THRESHOLD_SCENARIO_DETAILS: 'THRESHOLD_SCENARIO_DETAILS',
    WATER_LEAKAGE_SCENARIO_LANDING: 'WATER_LEAKAGE_SCENARIO_LANDING',
    WATER_LEAKAGE_SCENARIO_EDITION: 'WATER_LEAKAGE_SCENARIO_EDITION',
    WATER_LEAKAGE_SCENARIO_DETAILS: 'WATER_LEAKAGE_SCENARIO_DETAILS',
    DRIFT_SCENARIO_LANDING: 'DRIFT_SCENARIO_LANDING',
    DRIFT_SCENARIO_EDITION: 'DRIFT_SCENARIO_EDITION',
    DRIFT_SCENARIO_DETAILS: 'DRIFT_SCENARIO_DETAILS',
    ALERT_SCENARIO_REPORT_PLANS: 'ALERT_SCENARIO_REPORT_PLANS',
    ACTIONS_LIST: 'ACTIONS_LIST',
    ACTION_CONSULTATION: 'ACTION_CONSULTATION',
    AUTOMATED_REPORTS_LIST: 'AUTOMATED_REPORTS_LIST',
    AUTOMATED_REPORT_EDITION: 'AUTOMATED_REPORT_EDITION',
    SETTINGS: 'SETTINGS',
    USER_PROFILE: 'USER_PROFILE',
    DATA_EXPORT: 'DATA_EXPORT',
    MAPS: 'MAPS',
    DATA_VIEWER: 'DATA_VIEWER',
    CONNECTED_AS: 'CONNECTED_AS',
    DASHAVIZ: 'DASHAVIZ',
    BUILDINGS: 'BUILDINGS',
    BUILDING_DETAILS: 'BUILDING_DETAILS',
    METER_DETAILS: 'METER_DETAILS',
    DELIVERY_POINTS: 'DELIVERY_POINTS',
    USERS_PROFILES_MANAGEMENT: 'USERS_PROFILES_MANAGEMENT',
    CLASSIFICATIONS_MANAGEMENT: 'CLASSIFICATIONS_MANAGEMENT',
    PROJECT_SETTINGS: 'PROJECT_SETTINGS',
    ATTRIBUTES_MANAGEMENT: 'ATTRIBUTES_MANAGEMENT',
    IPMVP: 'IPMVP',
    IMPORT_HISTORY: 'IMPORT_HISTORY',
    BUSINESS_INDICATORS_MANAGEMENT: 'BUSINESS_INDICATORS_MANAGEMENT',
    BUSINESS_INDICATOR_EDITION: 'BUSINESS_INDICATOR_EDITION',
    BUSINESS_INDICATOR_REFERENCE_PERIODS_EDITION: 'BUSINESS_INDICATOR_REFERENCE_PERIODS_EDITION',
    BUSINESS_INDICATORS_DETAILS: 'BUSINESS_INDICATORS_DETAILS',
    ENTITY_MANAGEMENT: 'ENTITY_MANAGEMENT',
    DATA_HEALTH_SYNTHESIS: 'DATA_HEALTH_SYNTHESIS',
    DATA_HEALTH_TEMPORAL_COMPLETENESS: 'DATA_HEALTH_TEMPORAL_COMPLETENESS',
    DATA_HEALTH_TEMPORAL_COMPLETENESS_DETAILS: 'DATA_HEALTH_TEMPORAL_COMPLETENESS_DETAILS',
    DATA_HEALTH_COMPLETENESS: 'DATA_HEALTH_COMPLETENESS',
    DATA_HEALTH_QUALITY: 'DATA_HEALTH_QUALITY',
    DATA_QUALIFICATION_COMPLETENESS_DETAILS: 'DATA_QUALIFICATION_COMPLETENESS_DETAILS',
    DOCUMENT_MANAGEMENT: 'DOCUMENT_MANAGEMENT',
    DOCUMENT_MANAGEMENT_IMPORT: 'DOCUMENT_MANAGEMENT_IMPORT',
    INDICATORS_BUILDINGS_COMPARISON: 'INDICATORS_BUILDINGS_COMPARISON',
    SYSTEM_EDITION: 'SYSTEM_EDITION',
    VIRTUAL_SYSTEM_EDITION: 'VIRTUAL_SYSTEM_EDITION',
    VIRTUAL_SENSOR_EDITION: 'VIRTUAL_SENSOR_EDITION',
    CUSTOM_TILE_CONFIGURATION: 'CUSTOM_TILE_CONFIGURATION',
    MULTI_SENSOR_TILE_CREATION: 'MULTI_SENSOR_TILE_CREATION',
    SYSTEM_ADD: 'SystemAddPage',
    SYSTEM_ADD_ADVANCED: 'SystemAddAdvancedPage',
    VIRTUAL_SYSTEM_ADD: 'VirtualSystemAddPage',
    CONFIRM_PASSWORD: 'ConfirmPasswordPage',
    MANUAL_INDEX_READINGS: 'ManualIndexReadingsPage',
    SPECIFIC_MANUAL_INDEX_READINGS: 'SpecificManualIndexReadingsPage',
    BILLS_HOME: 'BillsHomePage',
    BILLS_BY_BUILDING: 'BillsByBuildingPage',
    BILLS_BY_BUILDING_ANALYSIS: 'BillsByBuildingAnalysisPage',
    BILLS_BY_DELIVERY_POINT: 'BillsByDeliveryPointPage',
    DELIVERY_POINT_ADD: 'DeliveryPointAddPage',
    SUPPLIER_PORTAL_ACCESS: 'SupplierPortalAccessPage',
    BILLS_IMPORT_FILES: 'BillsImportFilesPage',
    INVOICE_DISCREPANCY: 'InvoiceDiscrepancyPage',
    INVOICE_ERROR: 'INVOICE_ERROR',
    INVOICE_EDITION: 'InvoiceEditionPage',
    METERS_LIST: 'MetersListPage',
    METER_INDEX_INIT: 'MeterIndexInitPage',
    METERS_INDEX_STATEMENT: 'MeterIndexStatementPage',
    BUDGET_FUNNEL_VIEW: 'BudgetFunnelViewPage',
    BUDGET_EDITION: 'BudgetEditionPage',
    CONTRACT_POWER_OPTIMIZATION: 'ContractPowerOptimizationPage',
    SUPPLIERS_MISSING_INVOICES: 'SuppliersMissingInvoicesPage',
    IPE_HISTORY: 'IpeHistoryPage',
    BASELOAD_ANALYSIS: 'BASELOAD_ANALYSIS',
    BUILDING_BASELOAD_ANALYSIS: 'BUILDING_BASELOAD_ANALYSIS',
    CUSTOM_BUSINESS_INTELLIGENCE_ANALYSIS: 'CUSTOM_BUSINESS_INTELLIGENCE_ANALYSIS',
    DATABOARDS: 'DATABOARDS',
    EXPORT_LANDING: 'EXPORT_LANDING',
    EXPORT_EDITION: 'EXPORT_EDITION',
    GENERAL_CONDITIONS: 'GENERAL_CONDITIONS',
    PASSWORD_EXPIRED: 'PASSWORD_EXPIRED',
    TEST_DATASOURCE: 'TEST_DATASOURCE',
    TEST_DASHBOARD: 'TEST_DASHBOARD',
    HOMEPAGE: 'HOMEPAGE',
    SEN_ANALYSIS_LANDING: 'SEN_ANALYSIS_LANDING',
    SEN_ANALYSIS: 'SEN_ANALYSIS',
    DEET_EFA_LANDING: 'DEET_EFA_LANDING',
    RECOMMENDED_ACTIONS_LIST: 'RECOMMENDED_ACTIONS_LIST',
    IOT_SUPERVISION: 'IOT_SUPERVISION',
    MIP: 'MIP',
    MIP_PLAN_EDIT: 'MIP_PLAN_EDIT',
    MIP_SCENARIO_SYNTHESIS: 'MIP_SCENARIO_SYNTHESIS',
    ENERGY_SIGNATURE: 'ENERGY_SIGNATURE',
    THERMAL_COMFORT: 'THERMAL_COMFORT'
} as const;
/**
 * @deprecated
 */
export type Page = LiteralValues<typeof Page>;

export type PageParams<P extends Page> = {
    [Page.LOGIN]: {};
    [Page.ACCOUNT_CREATION]: {};
    [Page.DASHBOARDS]: {};
    [Page.BUILDING_CREATION]: {};
    [Page.NO_BUILDING_ACCESS_ERROR]: {};
    [Page.THRESHOLD_SCENARIO_LANDING]: {};
    [Page.THRESHOLD_SCENARIO_EDITION]: { alertScenarioId: AlertScenarioId };
    [Page.THRESHOLD_SCENARIO_DETAILS]: { alertScenarioId: AlertScenarioId; periodCode: AlertStatPeriodCode };
    [Page.WATER_LEAKAGE_SCENARIO_LANDING]: {};
    [Page.WATER_LEAKAGE_SCENARIO_EDITION]: { alertScenarioId: AlertScenarioId };
    [Page.WATER_LEAKAGE_SCENARIO_DETAILS]: { alertScenarioId: AlertScenarioId; periodCode: AlertStatPeriodCode };
    [Page.DRIFT_SCENARIO_LANDING]: {};
    [Page.DRIFT_SCENARIO_EDITION]: { alertScenarioId: AlertScenarioId };
    [Page.DRIFT_SCENARIO_DETAILS]: { alertScenarioId: AlertScenarioId; periodCode: AlertStatPeriodCode };
    [Page.ALERT_SCENARIO_REPORT_PLANS]: { alertScenarioId: AlertScenarioId };
    [Page.ACTIONS_LIST]: {};
    [Page.ACTION_CONSULTATION]: { actionId: string };
    [Page.AUTOMATED_REPORTS_LIST]: {};
    [Page.AUTOMATED_REPORT_EDITION]: { reportPlan?: ReportPlan };
    [Page.SETTINGS]: {};
    [Page.USER_PROFILE]: {};
    [Page.DATA_EXPORT]: {};
    [Page.MAPS]: {};
    [Page.DATA_VIEWER]: { tab: string };
    [Page.CONNECTED_AS]: {};
    [Page.DASHAVIZ]: {};
    [Page.BUILDINGS]: {};
    [Page.BUILDING_DETAILS]: { buildingId: BuildingId };
    [Page.METER_DETAILS]: { meterId: MeterId };
    [Page.DELIVERY_POINTS]: {};
    [Page.USERS_PROFILES_MANAGEMENT]: {};
    [Page.CLASSIFICATIONS_MANAGEMENT]: {};
    [Page.PROJECT_SETTINGS]: {};
    [Page.ATTRIBUTES_MANAGEMENT]: {};
    [Page.IPMVP]: {};
    [Page.IMPORT_HISTORY]: { resourceTypeInitialFilters?: ImportResourceType[] };
    [Page.BUSINESS_INDICATORS_DETAILS]: {};
    [Page.BUSINESS_INDICATORS_MANAGEMENT]: {};
    [Page.BUSINESS_INDICATOR_EDITION]: { businessIndicatorId: BusinessIndicatorId };
    [Page.BUSINESS_INDICATOR_REFERENCE_PERIODS_EDITION]: { businessIndicatorId: BusinessIndicatorId };
    [Page.ENTITY_MANAGEMENT]: {};
    [Page.DATA_HEALTH_SYNTHESIS]: {};
    [Page.DATA_HEALTH_TEMPORAL_COMPLETENESS]: {};
    [Page.DATA_HEALTH_TEMPORAL_COMPLETENESS_DETAILS]: {};
    [Page.DATA_HEALTH_COMPLETENESS]: {};
    [Page.DATA_HEALTH_QUALITY]: {};
    [Page.DATA_QUALIFICATION_COMPLETENESS_DETAILS]: {
        resourceType: CompletenessResourceType;
        resourceIds: string[];
        metric: DeliveryPointMetric;
        timestep: CompletenessTimeStep;
        periodIds: PrimitiveValueOrArray;
        universeIds: UniverseId[];
    };
    [Page.DOCUMENT_MANAGEMENT]: {};
    [Page.DOCUMENT_MANAGEMENT_IMPORT]: {};
    [Page.INDICATORS_BUILDINGS_COMPARISON]: {
        tab: string;
    };
    [Page.SYSTEM_EDITION]: {
        buildingUid: BuildingId;
        systemUid: SystemId;
    };
    [Page.VIRTUAL_SYSTEM_EDITION]: {
        buildingUid: BuildingId;
        systemUid: SystemId;
    };
    [Page.VIRTUAL_SENSOR_EDITION]: {
        buildingUid: BuildingId;
        systemUid: SystemId;
        sensorId?: string;
    };
    [Page.CUSTOM_TILE_CONFIGURATION]: { tileGroup: string };
    [Page.MULTI_SENSOR_TILE_CREATION]: { tileGroup: string };
    [Page.SYSTEM_ADD]: {};
    [Page.SYSTEM_ADD_ADVANCED]: {
        system: CoreSystemWithSensors;
    };
    [Page.VIRTUAL_SYSTEM_ADD]: {
        system: CoreSystemWithSensors;
    };
    [Page.CONFIRM_PASSWORD]: { email: string; token: string };
    [Page.MANUAL_INDEX_READINGS]: {};
    [Page.SPECIFIC_MANUAL_INDEX_READINGS]: { meterIds: MeterId[] };
    [Page.BILLS_HOME]: {};
    [Page.BILLS_BY_BUILDING]: {
        indicatorCode: TimeseriesIndicatorCode;
        dashboardRight: LegacyDashboardRight;
    };
    [Page.BILLS_BY_BUILDING_ANALYSIS]: {
        buildingId: BuildingId;
        universeId: UniverseId;
    };
    [Page.BILLS_BY_DELIVERY_POINT]: {
        deliveryPointUid: DeliveryPointId;
    };
    [Page.DELIVERY_POINT_ADD]: {};
    [Page.SUPPLIER_PORTAL_ACCESS]: {};
    [Page.BILLS_IMPORT_FILES]: {};
    [Page.INVOICE_DISCREPANCY]: {};
    [Page.INVOICE_ERROR]: {
        supplierId: string;
        invoiceReference: string;
    };
    [Page.INVOICE_EDITION]: {
        invoiceId: string;
    };
    [Page.METERS_LIST]: { buildingUid: BuildingId };
    [Page.METER_INDEX_INIT]: { meterId: MeterId };
    [Page.METERS_INDEX_STATEMENT]: { meterId: MeterId };
    [Page.BUDGET_FUNNEL_VIEW]: {
        timeseriesIndicator: TimeseriesIndicatorCode;
        dashboardRight: LegacyDashboardRight;
    };
    [Page.BUDGET_EDITION]: {};
    [Page.CONTRACT_POWER_OPTIMIZATION]: {};
    [Page.SUPPLIERS_MISSING_INVOICES]: {};
    [Page.IPE_HISTORY]: {
        buildingId: string;
        tileId: string;
    };
    [Page.TEST_DATASOURCE]: {};
    [Page.TEST_DASHBOARD]: {};
    [Page.BASELOAD_ANALYSIS]: {
        classificationNodeId?: ClassificationNodeId;
        filters?: BusinessIntelligenceFilter[];
        analysisKPI?: BaseloadKPI;
    };
    [Page.BUILDING_BASELOAD_ANALYSIS]: {
        buildingId: BuildingId;
        date: UnzonedDateTime;
        analysisKPI: BaseloadKPI;
    };
    [Page.CUSTOM_BUSINESS_INTELLIGENCE_ANALYSIS]: {
        classificationNodeId?: ClassificationNodeId;
        filters?: BusinessIntelligenceFilter[];
    };
    [Page.DATABOARDS]: {};
    [Page.EXPORT_LANDING]: {};
    [Page.EXPORT_EDITION]: { appExportId?: ExportId };
    [Page.GENERAL_CONDITIONS]: {};
    [Page.PASSWORD_EXPIRED]: {};
    [Page.HOMEPAGE]: {};
    [Page.SEN_ANALYSIS_LANDING]: { universeIds?: UniverseId[] };
    [Page.SEN_ANALYSIS]:
        | {
              buildingId: BuildingId;
              timeseriesIds: SensorTimeseriesId[];
          }
        | {
              analysisModelId: SenAnalysisModelId;
          };
    [Page.DEET_EFA_LANDING]: {};
    [Page.RECOMMENDED_ACTIONS_LIST]: {};
    [Page.IOT_SUPERVISION]: {};
    [Page.MIP]: {};
    [Page.MIP_PLAN_EDIT]: {
        planId: string;
        step?: number;
    };
    [Page.MIP_SCENARIO_SYNTHESIS]: {
        planId: string;
        scenarioId: string;
    };
    [Page.ENERGY_SIGNATURE]: {};
    [Page.THERMAL_COMFORT]: {};
}[P];

/**
 * This way of doing routing is not usual for angular
 * The purpose of this is to provide an abstraction layer over the native routing
 * to allow a smoother transition between ionic and a pure-angular app.
 *
 * After this transition is finished, we should think about getting rid of this abstraction
 */
export abstract class RoutingService {
    /**
     * @deprecated
     */
    abstract goTo<P extends Page>(page: P, pageParams: PageParams<P>): Promise<void>;

    abstract goBack(): void;

    /**
     * @deprecated
     */
    abstract canGoBack$(): Observable<boolean>;

    /**
     * @deprecated
     */
    abstract resetNavigationTo<P extends Page>(page: P, pageParams: PageParams<P>, extras?: NavigationExtras): Promise<void>;

    /**
     * @deprecated
     */
    abstract resetNavigationToHomePage(buildingId?: BuildingId): void;

    /**
     * @deprecated
     */
    abstract replace<P extends Page>(page: P, pageParams: PageParams<P>): void;

    /**
     * @deprecated
     */
    abstract getCurrentPage$(): Observable<Page>;

    /**
     * @deprecated
     */
    abstract listenToNavigationReset$(): Observable<void>;

    /**
     * @deprecated
     */
    abstract notifyNavigationReset(): void;

    abstract getActiveRoutesMergedData$(): Observable<any>;
}
